import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import backArrow from "../../assets/task/back_arrow.svg";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import Loader from "../../components/common/Loader";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import { ENTITY_FIELD_STATUSES } from "../../api/query";
import { BULK_UPSERT_ENTITY_FIELD_STATUS, BULK_UPDATE_ENTITY_STATUS } from "../../api/mutation";
import { getFieldDescription } from "./fieldDescriptions";

interface FieldState {
    id: number;
    has_issue: boolean;
    is_complete: boolean;
    need_validation: boolean;
    remarks: string;
}

const CLIENT_SHIFTS_FIELD_NAME = "Client Shifts";
const EMPLOYEE_SHIFTS_FIELD_NAME = "Employee Shifts";

/**
 * Fields that are not applicable for Case Management agencies.
 * They are auto-checked as complete and locked (disabled) for editing.
 * Stored lowercased + trimmed for case-insensitive matching.
 */
const CASE_MANAGEMENT_DISABLED_CLIENT_FIELDS_NORMALIZED = new Set([
    "plan of care : care tasks",
    "plan of care : care details",
    "referral information",
    "nurse assessment - dme",
    "nurse assessment - allergies",
    "nurse assessment - surgeries",
    "nurse assessment - height & weight",
    "nurse assessment - client info",
    "nurse assessment - advanced directive",
    "nurse assessment - rn assessment",
    "nurse assessment - client important dates",
]);

// Collapse multiple spaces into one before comparing, to handle DB inconsistencies
const isCaseManagementDisabledField = (fieldName: string) =>
    CASE_MANAGEMENT_DISABLED_CLIENT_FIELDS_NORMALIZED.has(
        fieldName.trim().toLowerCase().replace(/\s+/g, " ")
    );

/** Derive progress_status from field states.
 *  - all complete (incl. shifts field) → closed
 *  - all complete except shifts field  → verified
 *  - any has_issue                     → has_issue
 *  - none complete                     → not_started
 *  - otherwise                         → in_progress
 */
const deriveStatus = (fields: FieldState[], rawFields: any[], recordType: string): string => {
    if (!fields.length) return "not_started";
    const anyIssue = fields.some(f => f.has_issue);
    if (anyIssue) return "has_issue";
    const checkedCount = fields.filter(f => f.is_complete).length;
    if (checkedCount === 0) return "not_started";
    if (checkedCount === fields.length) return "closed";

    const shiftsFieldName = recordType === "employee" ? EMPLOYEE_SHIFTS_FIELD_NAME : CLIENT_SHIFTS_FIELD_NAME;

    const incompleteIds = fields.filter(f => !f.is_complete).map(f => f.id);
    const incompleteFieldNames = rawFields
        .filter(rf => incompleteIds.includes(Number(rf.id)))
        .map(rf => rf.field_name);
    const onlyShiftsIncomplete =
        incompleteFieldNames.length === 1 &&
        incompleteFieldNames[0] === shiftsFieldName;
    if (onlyShiftsIncomplete) return "verified";

    return "in_progress";
};

const RecordMigrationDetail: FunctionComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const agencyData = location.state?.agency;
    const recordType: string = location.state?.recordType || "client";
    const agencyType: string = location.state?.agency_type || "";
    const savedActiveTab = location.state?.activeTab;
    const savedSelectedMember = location.state?.selectedMember;

    // The entity passed from the list (real entity object)
    const initialEntity = location.state?.record;
    const passedEntities: any[] = location.state?.entities || [];
    const [selectedEntity, setSelectedEntity] = useState<any>(initialEntity);

    const [fieldStates, setFieldStates] = useState<Record<number, FieldState>>({});
    const [saving, setSaving] = useState(false);
    const [openInfoId, setOpenInfoId] = useState<number | null>(null);
    const [copied, setCopied] = useState(false);

    const handleCopyName = () => {
        const name = entityDisplayName(selectedEntity);
        if (!name) return;
        navigator.clipboard.writeText(name).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // ── Queries ───────────────────────────────────────────────────────────────
    // Field statuses for the selected entity
    const { data: fieldData, loading: fieldsLoading, refetch: refetchFields } = useQuery(ENTITY_FIELD_STATUSES, {
        variables: { entity_id: parseInt(selectedEntity?.id) },
        fetchPolicy: "no-cache",
        skip: !selectedEntity?.id,
    });

    // ── Mutations ─────────────────────────────────────────────────────────────
    const [upsertFields] = useMutation(BULK_UPSERT_ENTITY_FIELD_STATUS, { fetchPolicy: "no-cache" });
    const [updateStatus] = useMutation(BULK_UPDATE_ENTITY_STATUS, { fetchPolicy: "no-cache" });

    // ── Derived data ──────────────────────────────────────────────────────────
    const dropdownOptions = passedEntities.map((e: any) => ({
        key: e.id,
        name: [e.last_name, [e.first_name, e.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    }));

    const rawFields: any[] = fieldData?.entity_field_statuses?.field_statuses || [];

    // Pre-fill field states when data loads
    useEffect(() => {
        if (!rawFields.length) return;
        const isCaseManagement = agencyType.toLowerCase() === "case_management";
        const initial: Record<number, FieldState> = {};
        rawFields.forEach(f => {
            const isDisabled = isCaseManagement &&
                recordType === "client" &&
                isCaseManagementDisabledField(f.field_name);
            initial[Number(f.id)] = {
                id: Number(f.id),
                has_issue: isDisabled ? false : (f.has_issue ?? false),
                is_complete: isDisabled ? true : (f.is_complete ?? false),
                need_validation: isDisabled ? false : (f.need_validation ?? false),
                remarks: f.remarks ?? "",
            };
        });
        setFieldStates(initial);
    }, [fieldData]);

    // Reset when entity changes
    const handleEntitySelect = (key: any) => {
        const entity = passedEntities.find(e => String(e.id) === String(key));
        if (entity) {
            setSelectedEntity(entity);
            setFieldStates({});
        }
    };

    const updateField = (id: number, key: keyof Omit<FieldState, "id">, value: any) => {
        setFieldStates(prev => {
            const updated = { ...prev[id], [key]: value };
            if (key === "is_complete") {
                updated.need_validation = !value;
                if (value === true) updated.has_issue = false;
            }
            if (key === "has_issue" && value === true) {
                updated.is_complete = false;
                updated.need_validation = true;
            }
            return { ...prev, [id]: updated };
        });
    };

    const handleSave = async () => {
        if (!selectedEntity?.id) return;
        setSaving(true);
        try {
            const field_statuses = Object.values(fieldStates).map(f => ({
                id: f.id,
                has_issue: f.has_issue,
                is_complete: f.is_complete,
                need_validation: f.need_validation,
                remarks: f.remarks,
            }));

            // 1. Upsert field statuses
            const r1 = await upsertFields({
                variables: {
                    agency_id: parseInt(agencyData?.id),
                    entity_id: parseInt(selectedEntity.id),
                    entity_type: recordType,
                    field_statuses,
                },
            });
            const upsertResult = r1.data?.bulk_upsert_entity_field_status;
            if (upsertResult && upsertResult.status_code !== 200 && upsertResult.status_code !== 201) {
                showError(upsertResult.message || "Failed to save fields");
                return;
            }

            // 2. Derive and update entity progress status
            const progress_status = deriveStatus(Object.values(fieldStates), rawFields, recordType);
            const r2 = await updateStatus({
                variables: {
                    entity_ids: [parseInt(selectedEntity.id)],
                    progress_status,
                },
            });
            const statusResult = r2.data?.bulk_update_entity_status;
            if (statusResult && statusResult.status_code !== 200 && statusResult.status_code !== 201) {
                showError(statusResult.message || "Failed to update status");
                return;
            }

            showSuccess("Saved successfully");
            refetchFields();
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleBackNavigation = () => {
        navigate('/agency_migration_detail', {
            state: { agency: agencyData, activeTab: savedActiveTab, selectedMember: savedSelectedMember }
        });
    };

    const entityDisplayName = (e: any) =>
        [e?.last_name, [e?.first_name, e?.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ");

    return (
        <div className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-[360px] max-w-[100%] leading-[normal] tracking-[normal] text-left text-xl text-colors-text-black font-body-xs-semi-bold">
            <div className="self-stretch bg-gray overflow-hidden flex flex-col items-center justify-start relative gap-[24px] min-h-[913px] max-w-full">
                <div className="w-full flex flex-col items-start justify-start py-40 px-20 mq1490:px-[30px] mq575:px-5 box-border gap-[24px] max-w-[100%]">

                    {/* Header */}
                    <div className="self-stretch flex flex-row items-center gap-[12px] max-w-full">
                        <img
                            className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
                            alt="" src={backArrow}
                            onClick={handleBackNavigation}
                        />
                        <div className="flex-1 max-w-[400px]">
                            <SearchableInputsDropdown
                                label=""
                                placeHolder="Select record"
                                options={dropdownOptions}
                                initialValue={entityDisplayName(selectedEntity)}
                                onSelect={(key: any) => handleEntitySelect(key)}
                                dontselectlastValue={true}
                            />
                        </div>
                    </div>

                    {/* Copyable name display */}
                    {selectedEntity && (
                        <button
                            type="button"
                            onClick={handleCopyName}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors group -mt-2"
                            title="Click to copy name"
                        >
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 select-all">
                                {entityDisplayName(selectedEntity)}
                            </span>
                            {copied ? (
                                <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            )}
                            <span className="text-xs text-gray-400 group-hover:text-gray-500 shrink-0">
                                {copied ? "Copied!" : "Copy"}
                            </span>
                        </button>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-300 m-0">
                        {recordType.charAt(0).toUpperCase() + recordType.slice(1)} · {agencyData?.agency_name || "Agency"}
                    </p>

                    {/* Field Statuses */}
                    {fieldsLoading ? (
                        <div className="w-full flex justify-center py-12"><Loader /></div>
                    ) : rawFields.length === 0 ? (
                        <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
                            No fields found. Please sync data first.
                        </div>
                    ) : (
                        <div className="w-full flex flex-col gap-4">
                            {rawFields.map(field => {
                                const state = fieldStates[Number(field.id)];
                                if (!state) return null;
                                const isCaseManagementDisabled =
                                    agencyType.toLowerCase() === "case_management" &&
                                    recordType === "client" &&
                                    isCaseManagementDisabledField(field.field_name);
                                return (
                                    <div key={field.id} className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 ${isCaseManagementDisabled ? "opacity-60" : ""}`}>
                                        <div className="flex items-start justify-between gap-4 mb-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <h4 className="text-base font-bold text-gray-900 dark:text-white m-0">
                                                    {field.field_name}
                                                </h4>
                                                {isCaseManagementDisabled && (
                                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600">
                                                        N/A for Case Management
                                                    </span>
                                                )}
                                                {getFieldDescription(recordType, field.field_name) && (
                                                    <button
                                                        type="button"
                                                        className="shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 flex items-center justify-center hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                                        onClick={() => setOpenInfoId(openInfoId === Number(field.id) ? null : Number(field.id))}
                                                        title="View instructions"
                                                    >
                                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-6 shrink-0">
                                                <label className={`flex items-center gap-2 ${isCaseManagementDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                                    <input
                                                        type="checkbox"
                                                        className="w-5 h-5 accent-orange-500"
                                                        style={{ cursor: isCaseManagementDisabled ? "not-allowed" : "pointer" }}
                                                        checked={state.has_issue}
                                                        disabled={isCaseManagementDisabled}
                                                        onChange={e => updateField(Number(field.id), "has_issue", e.target.checked)}
                                                    />
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Issue</span>
                                                </label>
                                                <label className={`flex items-center gap-2 ${isCaseManagementDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                                    <input
                                                        type="checkbox"
                                                        className="w-5 h-5 accent-green-500"
                                                        style={{ cursor: isCaseManagementDisabled ? "not-allowed" : "pointer" }}
                                                        checked={state.is_complete}
                                                        disabled={isCaseManagementDisabled}
                                                        onChange={e => updateField(Number(field.id), "is_complete", e.target.checked)}
                                                    />
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Completed</span>
                                                </label>
                                            </div>
                                        </div>
                                        {openInfoId === Number(field.id) && getFieldDescription(recordType, field.field_name) && (
                                            <div className="mb-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200 whitespace-pre-line">
                                                {getFieldDescription(recordType, field.field_name)}
                                            </div>
                                        )}
                                        <textarea
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-y text-sm disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
                                            rows={3}
                                            placeholder={isCaseManagementDisabled ? "Not applicable for Case Management agencies" : "Add remarks..."}
                                            value={state.remarks}
                                            disabled={isCaseManagementDisabled}
                                            onChange={e => updateField(Number(field.id), "remarks", e.target.value)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="w-full flex justify-end gap-3 pt-4">
                        <button
                            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-semibold"
                            onClick={handleBackNavigation}
                        >
                            Cancel
                        </button>
                        <button
                            disabled={saving || fieldsLoading || !rawFields.length}
                            className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold disabled:opacity-60"
                            onClick={handleSave}
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecordMigrationDetail;
